import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Users, Gift, Crown, Settings, Megaphone, Palette, ScanLine, LogOut, Sparkles, Building2, FileText, Inbox, Wallet } from "lucide-react";

const adminLinks = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "side-admin-overview" },
  { to: "/admin/patients", label: "Patients", icon: Users, testid: "side-admin-patients" },
  { to: "/admin/checkin", label: "QR Check-in", icon: ScanLine, testid: "side-admin-checkin" },
  { to: "/admin/rewards", label: "Rewards", icon: Gift, testid: "side-admin-rewards" },
  { to: "/admin/tiers", label: "Tiers", icon: Crown, testid: "side-admin-tiers" },
  { to: "/admin/rules", label: "Loyalty Rules", icon: Settings, testid: "side-admin-rules" },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone, testid: "side-admin-campaigns" },
  { to: "/admin/branding", label: "Branding", icon: Palette, testid: "side-admin-branding" },
];

const superLinks = [
  { to: "/super-admin", label: "Overview", icon: LayoutDashboard, testid: "side-super-overview" },
  { to: "/super-admin/applications", label: "Applications", icon: Inbox, testid: "side-super-applications" },
  { to: "/super-admin/clinics", label: "Clinics", icon: Building2, testid: "side-super-clinics" },
  { to: "/super-admin/content", label: "Site content", icon: FileText, testid: "side-super-content" },
  { to: "/super-admin/wallet", label: "Apple Wallet", icon: Wallet, testid: "side-super-wallet" },
];

export default function AdminShell({ children, scope = "admin", title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const links = scope === "super" ? superLinks : adminLinks;

  const handleLogout = async () => {
    await logout();
    navigate(scope === "super" ? "/super-admin/login" : "/admin/login");
  };

  return (
    <div className="min-h-screen bg-mr-bg flex">
      <aside className="w-64 hidden lg:flex flex-col border-r border-mr bg-white">
        <div className="px-6 h-16 flex items-center gap-3 border-b border-mr">
          <div className="h-8 w-8 rounded-lg bg-mr-primary text-white flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <p className="font-display font-semibold text-[15px] text-mr-primary">MedRewards</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-mr-secondary">{scope === "super" ? "Platform" : "Clinic Console"}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => {
            const Active = location.pathname === l.to;
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${Active ? "bg-[#0F8964] text-white" : "text-mr-secondary hover:bg-[#F1F5F9] hover:text-mr-primary"}`}
              >
                <Icon className="h-4 w-4" />
                {l.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-mr">
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-mr-primary truncate">{user?.name}</p>
            <p className="text-xs text-mr-secondary truncate">{user?.email}</p>
          </div>
          <button
            data-testid="side-logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-mr-secondary hover:bg-[#E2E8F0] hover:text-mr-primary"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="glass-header h-16 sticky top-0 z-30 px-6 lg:px-10 flex items-center justify-between">
          <h1 className="font-display text-xl font-semibold text-mr-primary">{title}</h1>
          <span className="label-eyebrow text-mr-secondary">{scope === "super" ? "Super Admin" : "Admin"}</span>
        </div>
        <div className="p-6 lg:p-10">{children}</div>
      </main>
    </div>
  );
}
